import { DEFAULT_TIMEZONE } from "./time.js";

/** The todo fields the shared rules need. */
export interface TodoLike {
  important: boolean;
  dueDate: string | null;
  dueTime: string | null;
  completedAt: string | null;
  createdAt: string;
}

const byDue = (a: TodoLike, b: TodoLike) => {
  if (a.dueDate === b.dueDate) {
    // Same day: timed todos first (earliest first); no time = "sometime that day".
    if (a.dueTime === b.dueTime) return 0;
    if (a.dueTime === null) return 1;
    if (b.dueTime === null) return -1;
    return a.dueTime < b.dueTime ? -1 : 1;
  }
  if (a.dueDate === null) return 1;
  if (b.dueDate === null) return -1;
  return a.dueDate < b.dueDate ? -1 : 1;
};

const newestFirst = (a: string, b: string) => (a < b ? 1 : a > b ? -1 : 0);

/**
 * List order (spec01 FR7): open todos first (important, then by due date/time, no due date
 * last, then newest), then done todos (most recently completed first).
 */
export function compareTodos(a: TodoLike, b: TodoLike): number {
  const aDone = a.completedAt !== null;
  const bDone = b.completedAt !== null;
  if (aDone !== bDone) return aDone ? 1 : -1;
  if (aDone && bDone) return newestFirst(a.completedAt ?? "", b.completedAt ?? "");
  if (a.important !== b.important) return a.important ? -1 : 1;
  return byDue(a, b) || newestFirst(a.createdAt, b.createdAt);
}

/** Current local date (YYYY-MM-DD) and time (HH:MM) in a timezone. */
export function localNow(now: Date, timeZone: string = DEFAULT_TIMEZONE) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/**
 * An open todo is overdue once its due date has passed, or on the due date once its due time
 * has passed. A date without time is due "sometime that day", so it is overdue from the next day.
 */
export function isOverdue(todo: TodoLike, now: Date, timeZone: string = DEFAULT_TIMEZONE): boolean {
  if (todo.completedAt !== null || todo.dueDate === null) return false;
  const local = localNow(now, timeZone);
  if (todo.dueDate !== local.date) return todo.dueDate < local.date;
  return todo.dueTime !== null && todo.dueTime <= local.time;
}
